<template>
  <AdminLayout>
    <Head title="Company Locations" />

    <template #pageTitle> Locations </template>

    <template #headingOptions>
      <button
        @click="showingCreatePrompt = true"
        type="button"
        class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-4 w-4"
          aria-hidden="true"
        />
        Add Location
      </button>

      <Popover class="relative">
        <PopoverButton class="btn btn-secondary-outline py-3"> Sort by </PopoverButton>

        <PopoverPanel class="absolute z-50 p-1 md:right-0">
          <div class="menu w-32">
            <div class="py-1">
              <Link
                :href="getSortUrl('name', 'asc')"
                :class="[isSortActive('name', 'asc', true) ? 'active' : '', 'menu-item menu-link']"
              >
                Name (A-Z)
              </Link>
              <Link
                :href="getSortUrl('name', 'desc')"
                :class="[isSortActive('name', 'desc') ? 'active' : '', 'menu-item menu-link']"
              >
                Name (Z-A)
              </Link>
            </div>
          </div>
        </PopoverPanel>
      </Popover>
    </template>

    <div class="flex-1 bg-gray-100">
      <data-table
        :items="locations.data"
        :headers="headers"
        :actions="[]"
        :selectable="false"
      >
        <template v-slot:rows="slotRow">
          <index-row
            v-slot:rows="slotRow"
            v-bind="slotRow"
            :show-route="showLocationRoute"
            @remove="openDeletePrompt"
          />
        </template>
      </data-table>
    </div>

    <Pagination
      :page="locations.meta.current_page"
      :total="locations.meta.total"
      :per-page="locations.meta.per_page"
      :path="locations.meta.path"
      :params="queryParams"
      v-if="locations.meta.last_page > 1"
    />

    <template #modals>
      <SmallModal
        :is-open="showingCreatePrompt"
        @close="showingCreatePrompt = false"
      >
        <template #header> Create Location </template>

        <div class="mt-4">
          <div class="input-group">
            <label for="name"> Name </label>
            <input
              v-model="createForm.name"
              type="text"
              name="name"
              id="name"
              placeholder="Enter location name"
            />
            <p
              v-if="getErrors('name', createForm)"
              class="input-error"
            >
              {{ getErrors('name', createForm) }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
            @click="createLocation()"
          >
            Add Location
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingCreatePrompt = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingDeletePrompt"
        @close="showingDeletePrompt = false"
      >
        <template #header> Deleting Location </template>

        <div class="mt-4">
          <div class="input-group">
            <label> You are about to delete the following location: </label>
            <p class="mt-2 font-bold">
              {{ locationToDelete.name }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-danger"
            @click="deleteLocation()"
          >
            Delete
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingDeletePrompt = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>
    </template>
  </AdminLayout>
</template>

<script>
import { Head, Link, useForm } from '@inertiajs/vue3'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { DotsHorizontalIcon } from '@heroicons/vue/solid'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import Pagination from '@/Components/Pagination.vue'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import dateFilters from '@/Mixins/dateFilters'
import isUrlAware from '@/Mixins/isUrlAware'
import checksErrors from '@/Mixins/checksErrors'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import { PlusSmIcon as PlusSmIconOutline } from '@heroicons/vue/outline'
import DataTable from '@/Components/Table/DataTable'
import IndexRow from '@/Pages/Company/Locations/Components/IndexRow'

export default {
  mixins: [dateFilters, isUrlAware, checksErrors],

  components: {
    AdminLayout,
    Head,
    Link,
    Pagination,
    DotsHorizontalIcon,
    Popover,
    PopoverButton,
    PopoverPanel,
    SmallModal,
    PlusSmIconOutline,
    PlusSmIconSolid,
    DataTable,
    IndexRow
  },

  props: {
    locations: Object
  },

  data() {
    return {
      showingCreatePrompt: false,
      showingDeletePrompt: false,
      locationToDelete: null
    }
  },

  setup() {
    const createForm = useForm({
      name: ''
    })
    const deleteForm = useForm({})

    return {
      createForm,
      deleteForm
    }
  },

  computed: {
    headers() {
      return ['Name', 'Address', 'License #', 'Type', 'Creation Date', 'Updated Date']
    },
    showLocationRoute() {
      return (item) => {
        return route('company.locations.edit', { location: item.id })
      }
    }
  },

  methods: {
    getSortUrl(sort, dir = 'asc') {
      const url = new URL(this.locations.meta.path)

      url.searchParams.set('sort', sort)
      url.searchParams.set('dir', dir)

      return url.toString()
    },
    isSortActive(sort, dir = 'asc', isDefault = false) {
      const urlSort = this.queryParam('sort')
      const urlDir = this.queryParam('dir') || 'asc'

      if (urlSort) {
        return sort === urlSort && dir === urlDir
      }

      return isDefault
    },
    createLocation() {
      this.createForm.post(route('company.locations.store'), {
        onSuccess: () => {
          this.createForm.reset('name')
        }
      })
    },
    openDeletePrompt(location) {
      this.locationToDelete = location
      this.showingDeletePrompt = true
    },
    deleteLocation() {
      if (!this.locationToDelete) {
        return
      }

      this.deleteForm.delete(route('company.locations.destroy', { location: this.locationToDelete.id }), {
        onSuccess: () => {}
      })
    }
  }
}
</script>
